<template>
  <Section v-loading="loading" id="multiple-barcode-view">
    <div v-if="showBarcodes">
      <div class="inner-navbar">
        <el-form
          :inline="true"
          class="barcode-form-inline d-flex align-items-center"
        >
          <el-form-item>
            <el-button
              :disabled="disablePrint"
              @click="onPrint"
              v-if="showPrint"
              :title="printButtonTitle"
              type="button"
              class="print-btn"
            >
              <span class="el-icon-printer"></span>Print Barcodes
            </el-button>
          </el-form-item>
        </el-form>
      </div>
      <div class="inner-content">
        <div class id="barcodesBody">
          <div
            v-for="(barcode, index) in generatedBarcodes"
            :key="index"
            style="margin-top: 20px"
            class="each-barcode"
          >
            <div class="barcode">
              <div class="show-barcode">
                <el-row>
                  <el-col :span="24">
                    <div class="barcode-body" id="barcodeBody">
                      <div class="bar-code-table">
                        <div
                          class="bar-code-table-inner"
                          style="padding: 5px 10px"
                        >
                          <table
                            style="width: 100%; padding: 1em; max-width: 550px"
                          >
                            <tbody>
                              <tr style="vertical-align: middle">
                                <th
                                  class="patient-name-block"
                                  style="
                                    text-align: left;
                                    text-transform: uppercase;
                                  "
                                >
                                  <div
                                    class="patient-name-single-line"
                                    v-if="barcode.patient_info"
                                    style="display: flex; align-items: center; gap: 10px;"
                                  >
                                    <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1;">
                                      {{ barcode.patient_info.first_name }}
                                      {{
                                        barcode.patient_info.middle_name
                                          ? barcode.patient_info.middle_name
                                          : ""
                                      }}
                                      {{ barcode.patient_info.last_name }}
                                    </span>
                                    <span style="white-space: nowrap; font-size: 10px; flex-shrink: 0;">
                                      DOB:
                                      {{
                                        dateWithMomentUTC(
                                          barcode.patient_info.date_of_birth
                                        )
                                      }}
                                    </span>
                                  </div>
                                </th>
                              </tr>
                            </tbody>
                          </table>
                          <div class="content" style="margin-top: 10px;">
                            <el-row>
                              <el-col :span="24">
                                <div class="image">
                                  <BarcodeGenerator
                                    :value="barcode.barcode_value"
                                    font="arial"
                                    font-size="18"
                                    height="76"
                                    :width="1.4"
                                    display-value="false"
                                    :margin="0"
                                    v-if="barcode.barcode_value"
                                  ></BarcodeGenerator>
                                </div>
                                <div
                                  class="assession-id-with-mcode"
                                  style="
                                    text-align: center;
                                    font-size: 11.953325px !important;
                                    font-weight: 600;
                                    letter-spacing: 0.15pt;
                                    line-height: 1;
                                    display: flex;
                                    align-items: center;
                                    justify-content: flex-start;
                                    flex-wrap: nowrap;
                                    width: 100%;
                                    gap: 8px;
                                  "
                                >
                                  <span style="flex-shrink: 0;">{{ barcode.display_value }}</span>
                                  <span
                                    v-if="barcode.instrument"
                                    :title="barcode.instrument"
                                    style="font-size: 9px; font-weight: 500; flex: 1 1 auto; min-width: 0; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                                  >
                                    {{ barcode.instrument }}
                                  </span>
                                </div>
                              </el-col>
                            </el-row>
                          </div>
                        </div>
                      </div>
                    </div>
                  </el-col>
                </el-row>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="no-barcode"
      v-if="!generatedBarcodes.length && !loading"
    >
      <p>Unable to generate barcodes.</p>
    </div>
  </Section>
</template>

<script>
import { mapGetters } from "vuex";
import caseDataHelper from "@/mixins/caseDataHelper";

import BarcodeGenerator from "./BarcodeGenerator.vue";
import { SAMPLE_TYPE_SUFFIXES } from "@/config/constants/barcodeConstants";

export default {
  mixins: [caseDataHelper],
  components: {
    BarcodeGenerator,
  },
  computed: {
    ...mapGetters("orders", [
      "getTestgoOrderBarcodeSources",
      "getTestgoOrderBarcodeStatus",
    ]),
    ...mapGetters("profile", ["getProfileData"]),
    printButtonTitle() {
      return this.disablePrint ? "Wait until images are ready" : "";
    },
  },
  data() {
    return {
      showBarcodes: false,
      showPrint: false,
      disablePrint: false,
      loading: false,
      generatedBarcodes: [],
    };
  },
  async mounted() {
    await this.generateBarcodes();
  },
  methods: {
    // Suffix appended to the order id on the barcode, derived from the tube.
    getTubeSuffix(tubeName) {
      const tube = (tubeName || "").toLowerCase();
      if (tube.includes("urine")) return SAMPLE_TYPE_SUFFIXES.URINE;
      if (tube.includes("plasma")) return SAMPLE_TYPE_SUFFIXES.PLASMA;
      if (
        tube.includes("serum") ||
        tube.includes("sst") ||
        tube.includes("gold")
      ) {
        return SAMPLE_TYPE_SUFFIXES.SERUM;
      }
      return "";
    },
    instrumentDisplay(instrument) {
      return instrument || "";
    },
    buildBarcodes() {
      const list = [];
      (this.getTestgoOrderBarcodeSources || []).forEach((src) => {
        const info = src.order_information || {};
        const orderId = info.order_id;
        if (!orderId) return;
        // One barcode per tube; fall back to a single order-level barcode.
        const tubes =
          src.tubes && src.tubes.length
            ? src.tubes
            : [{ tube_name: null, instrument: null }];
        tubes.forEach((tube) => {
          const suffix = this.getTubeSuffix(tube.tube_name);
          const value = suffix ? `${orderId}-${suffix}` : orderId;
          list.push({
            barcode_value: value,
            display_value: value,
            instrument: this.instrumentDisplay(tube.instrument),
            patient_info: info.patient_info,
          });
        });
      });
      this.generatedBarcodes = list;
    },
    async generateBarcodes() {
      this.loading = true;
      try {
        const params = {};
        if (this.$route.query.order_ids) {
          const ids = this.$route.query.order_ids;
          params.order_ids = typeof ids === "string" ? [ids] : ids;
        } else {
          this.loading = false;
          return;
        }
        params.key = "BAR_CODES";
        await this.$store.dispatch("orders/generateTestgoOrderBarcodes", params);

        if (this.getTestgoOrderBarcodeStatus) {
          this.buildBarcodes();
          this.showBarcodes = true;
          this.showPrint = true;
          this.disablePrint = true;
          setTimeout(() => {
            this.disablePrint = false;
          }, 2000);
        } else {
          this.$notify({
            type: "Error",
            title: "Failed",
            message: "Failed to generate barcodes",
          });
        }
        // eslint-disable-next-line no-empty
      } catch (err) {
        console.log(err);
      }
      this.loading = false;
    },
    async onPrint() {
      window.print();
    },
  },
  unmounted() {
    this.$store.dispatch("orders/resetGenerateTestgoOrderBarcodes");
  },
};
</script>

<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700;800&display=swap");
#multiple-barcode-view {
  padding: 2em 0;
  font-family: "Open Sans", sans-serif;
  @page {
    size: A4;
    margin: 0 auto;
    width: 288px;
    height: 192px;
  }
  .number-counter {
    .el-input__inner {
      padding-right: 0 !important;
      font-weight: 500;
      color: #000;
      font-size: 15px;
    }
  }
  .each-barcode {
    margin-top: 0 !important;
    page-break-inside: avoid;
    page-break-after: avoid;
  }
  .barcode-form-inline {
    .el-form-item {
      margin-bottom: 0;
    }
    margin-bottom: 45px;
  }
  #barcodeBody {
    .bar-code-table {
      width: 288px;
      margin: 4.85em auto;
      height: 192px;
      overflow: hidden;
      padding: 0;
      transform: rotate(90deg);
      table {
        padding: 10px 5px 0 10px !important;
      }
      th {
        font-size: 14px;
      }
      .image {
        img {
          max-width: 200px;
          margin: 0 auto;
        }
      }
    }
  }
  .inner-navbar {
    display: flex;
    justify-content: flex-end;
    margin-top: 30px;
    margin-right: 10px;
    .print-btn {
      background: #6f52ed;
      padding: 9px 15px;
      color: #fff;
      font-size: 1em;
      span {
        padding-right: 5px;
      }
    }
  }
  .inner-content {
    .bar-code-table {
      background: #fff;
      padding: 10px;
      .image {
        display: table;
        margin: 0 auto;
        max-width: 100% !important;
      }
      table {
        margin: 0 auto;
      }
    }
  }
  .each-barcode {
    display: table;
    margin: 20px auto;
    margin-bottom: 20px;
  }
  @media print {
    .each-barcode {
      margin: 0 !important;
      display: block !important;
    }
    .inner-navbar {
      display: none !important;
      margin-top: 0 !important;
      margin-right: 0 !important;
    }
  }

  #barcodeBody .bar-code-table {
    page-break-before: always;
    margin-bottom: 45px !important;
    margin-left: -48px !important;
    margin-right: 0 !important;
    background: #fff !important;
    margin-top: 48px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  #barcodeBody .bar-code-table .image img {
    margin-bottom: -5px;
  }
  #barcodeBody .bar-code-table .assession-id-with-mcode {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 8px;
    text-align: center;
    font-size: 11.953325px !important;
    font-weight: 600;
    letter-spacing: 0.15pt;
    line-height: 1;
  }
  #barcodeBody .bar-code-table .assession-id-with-mcode span {
    display: inline-block;
  }
  #barcodeBody .bar-code-table th {
    font-size: 7px !important;
    font-weight: normal;
    letter-spacing: 0.1px;
  }
  #barcodeBody .bar-code-table .patient-name-block {
    padding-top: 10px;
  }
  #barcodeBody .bar-code-table .patient-name-single-line {
    display: flex;
    align-items: center;
    gap: 15px;
  }
  #barcodeBody .bar-code-table .patient-name-single-line span:first-child {
    font-size: 11px;
    font-weight: 500;
    line-height: 11px;
    letter-spacing: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  #barcodeBody .bar-code-table .patient-name-single-line span:last-child {
    font-size: 10px;
    font-weight: 500;
    line-height: 11px;
    letter-spacing: 0;
    flex-shrink: 0;
    white-space: nowrap;
  }
  .bar-code-table-inner {
    width: 100%;
    padding: 10px 10px 6px 30px !important;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .patient-name-block {
    padding-left: 15px;
  }
  padding-top: 0 !important;

  #multiple-barcode-view {
    padding-top: 0;
  }
}
.no-barcode {
  text-align: center;
  font-size: 1.1em;
}
.bar-code-table-inner {
  padding-bottom: 10px !important;
}
</style>
